import React, { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react"
import TourModal, { TourModalStep } from "./TourModal"

export interface TourModalRegistration {
    modalId: string
    steps: TourModalStep[]
}

interface TourModalContextValue {
    registerModalTour: (registration: TourModalRegistration) => () => void
    startModalTour: (modalId?: string) => void
    isModalTourAvailable: (modalId?: string) => boolean
}

const TourModalContext = createContext<TourModalContextValue | undefined>(undefined)

export const TourModalProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
    const [registrations, setRegistrations] = useState<Record<string, TourModalRegistration>>({})
    const [activeModalId, setActiveModalId] = useState<string>()
    const [isOpen, setIsOpen] = useState(false)

    const registerModalTour = useCallback((registration: TourModalRegistration) => {
        setRegistrations((prev) => ({
            ...prev,
            [registration.modalId]: registration,
        }))

        return () => {
            setRegistrations((prev) => {
                const next = { ...prev }
                delete next[registration.modalId]
                return next
            })
        }
    }, [])

    const startModalTour = useCallback((modalId?: string) => {
        if (!modalId || !registrations[modalId]) return

        setActiveModalId(modalId)
        setIsOpen(true)
    }, [registrations])

    const isModalTourAvailable = useCallback((modalId?: string) => {
        return Boolean(modalId && registrations[modalId])
    }, [registrations])

    const activeSteps = activeModalId ? registrations[activeModalId]?.steps ?? [] : []

    const value = useMemo<TourModalContextValue>(() => ({
        registerModalTour,
        startModalTour,
        isModalTourAvailable,
    }), [registerModalTour, startModalTour, isModalTourAvailable])

    return (
        <TourModalContext.Provider value={value}>
            {children}
            <TourModal
                open={isOpen}
                onClose={() => setIsOpen(false)}
                steps={activeSteps}
                zIndex={10000}
            />
        </TourModalContext.Provider>
    )
}

export const useTourModalEducation = () => {
    const context = useContext(TourModalContext)

    if (!context) {
        throw new Error("useTourModalEducation must be used inside TourModalProvider")
    }

    return context
}

export const useOptionalTourModalEducation = () => {
    return useContext(TourModalContext)
}
